import * as React from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';

export interface ProgressProps
  extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  value?: number;
  max?: number;
  variant?: 'default' | 'success' | 'warning' | 'destructive';
  size?: 'sm' | 'md' | 'lg';
  showValue?: boolean;
  animated?: boolean;
}

const variantStyles = {
  default: 'bg-[var(--color-primary)]',
  success: 'bg-[var(--color-accent)]',
  warning: 'bg-[var(--color-warning)]',
  destructive: 'bg-[var(--color-destructive)]',
};

const sizeStyles = {
  sm: 'h-1',
  md: 'h-2',
  lg: 'h-3',
};

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(
  (
    {
      className,
      value = 0,
      max = 100,
      variant = 'default',
      size = 'md',
      showValue = false,
      animated = true,
      ...props
    },
    ref
  ) => {
    const percentage = Math.min(100, Math.max(0, (value / max) * 100));

    return (
      <div className={`relative ${showValue ? 'pb-5' : ''}`}>
        <ProgressPrimitive.Root
          ref={ref}
          className={`
            progress overflow-hidden ${sizeStyles[size]}
            ${className || ''}
          `}
          value={value}
          max={max}
          {...props}
        >
          <ProgressPrimitive.Indicator
            className={`
              progress-bar h-full w-full flex-1 transition-all duration-300 ease-out
              ${variantStyles[variant]}
              ${animated ? 'progress-bar-animated' : ''}
            `}
            style={{ transform: `translateX(-${100 - percentage}%)` }}
          />
        </ProgressPrimitive.Root>
        {showValue && (
          <span className="absolute bottom-0 right-0 text-xs text-muted-foreground">
            {Math.round(percentage)}%
          </span>
        )}
      </div>
    );
  }
);
Progress.displayName = ProgressPrimitive.Root.displayName;

export { Progress };
